import { z } from "@wedding/shared";
import type { ComponentType } from "react";

import { CinematicLayout } from "./cinematic";
import { MinimalLayout } from "./minimal";
import { ParallaxLayout } from "./parallax";
import { EditorialLayout } from "./editorial";
import { DarkRomanceLayout } from "./dark-romance";
import { AtelierLayout } from "./atelier";
import { OnyxLayout } from "./onyx";
import { VelvetLayout } from "./velvet";
import { PrismLayout } from "./prism";
import { VeilLayout } from "./veil";
import { EclipseLayout } from "./eclipse";
import { MirageLayout } from "./mirage";
import { AurumLayout } from "./aurum";
import { SonataLayout } from "./sonata";
import { NebulaLayout } from "./nebula";
import { TideLayout } from "./tide";
import { VedetteLayout } from "./vedette";
import { StrataLayout } from "./strata";
import { ApparitionLayout } from "./apparition";
import { AetheriaLayout } from "./aetheria";

import { type OnyxConfig, ONYX_DEFAULTS, onyxConfigSchema } from "./onyx/config";
import { collectOnyxImages } from "./onyx/media";
import { OnyxLoader } from "./onyx/OnyxLoader";
import {
  APPARITION_DEFAULTS,
  apparitionConfigSchema,
  AETHERIA_DEFAULTS,
  aetheriaConfigSchema,
} from "@wedding/shared";
import type { JoinLayoutProps, LayoutDefinition, LoaderProps, CollectMediaArgs } from "./types";

export type { JoinLayoutProps, LayoutDefinition, LoaderProps, CollectMediaArgs } from "./types";
export { MOCK_WEDDING, DEMO_VIDEOS, DEMO_IMAGES } from "./mock-data";

export interface JoinLayoutDefinition {
  id: string;
  label: string;
  tier: "original" | "premium";
  /** Layout component — may optionally accept a `config` prop. */
  component: ComponentType<JoinLayoutProps & { config?: unknown }>;
  /** Defaults for layouts that have been migrated to LayoutDefinition (config-driven). */
  defaults?: unknown;
  /** Zod schema for the config (only present on migrated layouts). */
  schema?: z.ZodType<unknown>;
  /**
   * This theme's own loader. Themes without one fall back to GenericLoader
   * (see PremiumLayoutHost). Per-theme loaders are the OOP goal.
   */
  loader?: ComponentType<LoaderProps>;
  /**
   * Enumerates the media the host should preload before mounting this theme.
   * Default (host): gallery images + cover + ambient video. Themes with extra
   * media (e.g. Onyx lookbook look images) override this.
   */
  collectMedia?: (args: CollectMediaArgs) => { images: string[]; video?: string };
}

// Legacy layouts — not yet config-driven. They render their hardcoded demo values.
const legacyEntry = (
  id: string,
  label: string,
  tier: "original" | "premium",
  component: ComponentType<JoinLayoutProps>,
): JoinLayoutDefinition => ({
  id,
  label,
  tier,
  component: component as ComponentType<JoinLayoutProps & { config?: unknown }>,
});

// Onyx is the first layout to use the full OOP LayoutDefinition pattern.
const onyxDef: LayoutDefinition<typeof ONYX_DEFAULTS> = {
  id: "onyx",
  label: "Onyx",
  tier: "premium",
  component: OnyxLayout,
  defaults: ONYX_DEFAULTS,
  schema: onyxConfigSchema,
};

const apparitionDef: LayoutDefinition<typeof APPARITION_DEFAULTS> = {
  id: "apparition",
  label: "Apparition",
  tier: "premium",
  component: ApparitionLayout,
  defaults: APPARITION_DEFAULTS,
  schema: apparitionConfigSchema,
};

const aetheriaDef: LayoutDefinition<typeof AETHERIA_DEFAULTS> = {
  id: "aetheria",
  label: "Aetheria",
  tier: "premium",
  component: AetheriaLayout,
  defaults: AETHERIA_DEFAULTS,
  schema: aetheriaConfigSchema,
};

export const JOIN_LAYOUTS: JoinLayoutDefinition[] = [
  // ── Original 5 ──
  legacyEntry("cinematic", "Sinematik", "original", CinematicLayout),
  legacyEntry("minimal", "Minimal", "original", MinimalLayout),
  legacyEntry("parallax", "Romantik Parallax", "original", ParallaxLayout),
  legacyEntry("editorial", "Bold Editorial", "original", EditorialLayout),
  legacyEntry("dark-romance", "Dark Romance", "original", DarkRomanceLayout),

  // ── Premium 13 ──
  legacyEntry("atelier", "Atelier", "premium", AtelierLayout),
  legacyEntry("velvet", "Velvet", "premium", VelvetLayout),
  {
    ...onyxDef,
    component: onyxDef.component as ComponentType<JoinLayoutProps & { config?: unknown }>,
    schema: onyxDef.schema as z.ZodType<unknown>,
    loader: OnyxLoader,
    collectMedia: ({ config, galleryImages, ambientVideoUrl }: CollectMediaArgs) => {
      // Bileşenin render edeceği BOYUTLU URL'leri preload et — loader tam da
      // ekranda görünecek türevleri ısıtsın, render'la birebir eşleşsin
      // (yoksa lookbook'ta kaydırırken siyah→"pat" beliriş).
      const images = collectOnyxImages(config as OnyxConfig | undefined, galleryImages);
      return { images, video: ambientVideoUrl };
    },
  },
  legacyEntry("prism", "Prism", "premium", PrismLayout),
  legacyEntry("veil", "Veil", "premium", VeilLayout),
  legacyEntry("eclipse", "Eclipse", "premium", EclipseLayout),
  legacyEntry("mirage", "Mirage", "premium", MirageLayout),
  legacyEntry("aurum", "Aurum", "premium", AurumLayout),
  legacyEntry("sonata", "Sonata", "premium", SonataLayout),
  legacyEntry("nebula", "Nebula", "premium", NebulaLayout),
  legacyEntry("tide", "Tide", "premium", TideLayout),
  legacyEntry("vedette", "Vedette", "premium", VedetteLayout),
  legacyEntry("strata", "Strata", "premium", StrataLayout),
  {
    ...apparitionDef,
    component: apparitionDef.component as ComponentType<JoinLayoutProps & { config?: unknown }>,
    schema: apparitionDef.schema as z.ZodType<unknown>,
  },
  {
    ...aetheriaDef,
    component: aetheriaDef.component as ComponentType<JoinLayoutProps & { config?: unknown }>,
    schema: aetheriaDef.schema as z.ZodType<unknown>,
  },
];

export type JoinLayoutId =
  | "default"
  | "cinematic" | "minimal" | "parallax" | "editorial" | "dark-romance"
  | "atelier" | "velvet" | "onyx" | "prism" | "veil"
  | "eclipse" | "mirage" | "aurum" | "sonata" | "nebula" | "tide"
  | "vedette" | "strata" | "apparition" | "aetheria";

export function getLayoutById(id: string | undefined): JoinLayoutDefinition | undefined {
  if (!id) return undefined;
  return JOIN_LAYOUTS.find((l) => l.id === id);
}
